'use client';

import { useState, useEffect, useRef } from 'react';
import { getConsent, setConsent, isAnalyticsEnabledByEnv } from '@/lib/analytics';
import { Button } from '@/components/atoms/Button';
import { cn } from '@/components/atoms/primitives/utils';

const COOKIE_CONSENT_STORAGE_KEY = 'loci_cookie_consent_dismissed';

/**
 * Fallback minimum height (in px) for the layout spacer that offsets the
 * fixed-position banner on mobile. Used at first paint before the banner
 * mounts and ResizeObserver reports its actual height. Picked to match the
 * banner's stacked mobile layout (paragraph + two buttons + padding). Once
 * the banner mounts the spacer height is replaced with the measured value.
 */
const FALLBACK_BANNER_HEIGHT_PX = 160;

/**
 * WEB-1109: the banner's live height, published on `<html>` while it shows.
 * From 768 the toast stack sits in the bottom-left corner, under this bar's
 * z (1300 > `--z-toast`); `--toast-inset-bottom` reads this to clear it.
 * The `:root` token declares the `0px` fallback for when no banner shows.
 */
export const CONSENT_BAR_HEIGHT_VAR = '--consent-bar-h';

/**
 * Cookie/analytics consent banner for GDPR/CCPA.
 * Shown only when analytics is enabled and user has not yet made a choice.
 *
 * WEB-307: While visible, renders a sibling spacer that reserves vertical
 * space equal to the banner's measured height so the fixed banner does not
 * overlap page content (e.g. the last card in `/neighborhoods` or the
 * `CityFooter`). The spacer is removed on dismiss alongside the banner.
 *
 * It also publishes that height for the toast stack ({@link CONSENT_BAR_HEIGHT_VAR}).
 *
 * Positioning: flush to the viewport bottom at every breakpoint (`bottom-0`).
 * Safe-area inset is applied as extra bottom padding so the banner's edge
 * stays viewport-aligned on notched devices while tap targets clear the
 * home indicator.
 */
export function CookieConsent(): React.ReactElement | null {
  const [mounted, setMounted] = useState(false);
  const [showBanner, setShowBanner] = useState(false);
  const [bannerHeight, setBannerHeight] = useState(FALLBACK_BANNER_HEIGHT_PX);
  const bannerRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    setMounted(true);
  }, []);

  useEffect(() => {
    if (!mounted || !isAnalyticsEnabledByEnv()) {
      setShowBanner(false);
      return;
    }
    const consent = getConsent();
    const dismissed =
      typeof localStorage !== 'undefined' && localStorage.getItem(COOKIE_CONSENT_STORAGE_KEY);
    setShowBanner(consent === null && !dismissed);
  }, [mounted]);

  // Observe the banner's rendered height so the spacer matches it exactly
  // (and the toast stack clears it — `CONSENT_BAR_HEIGHT_VAR`), including the
  // responsive layout shift at the `sm` breakpoint (column on mobile, row on
  // >= sm). Fallback height carries us through the first frame before
  // measurement lands.
  useEffect(() => {
    if (!showBanner) return;
    const el = bannerRef.current;
    if (!el) return;
    const root = document.documentElement;

    const update = (): void => {
      const height = el.offsetHeight || FALLBACK_BANNER_HEIGHT_PX;
      setBannerHeight(height);
      root.style.setProperty(CONSENT_BAR_HEIGHT_VAR, `${height}px`);
    };
    update();

    const observer = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(update);
    observer?.observe(el);
    return () => {
      observer?.disconnect();
      // Dismissed or unmounted: fall back to the `:root` `0px`.
      root.style.removeProperty(CONSENT_BAR_HEIGHT_VAR);
    };
  }, [showBanner]);

  const handleAccept = (): void => {
    setConsent(true);
    try {
      localStorage.setItem(COOKIE_CONSENT_STORAGE_KEY, 'true');
    } catch {
      // ignore
    }
    setShowBanner(false);
  };

  const handleReject = (): void => {
    setConsent(false);
    try {
      localStorage.setItem(COOKIE_CONSENT_STORAGE_KEY, 'true');
    } catch {
      // ignore
    }
    setShowBanner(false);
  };

  if (!showBanner) return null;

  return (
    <>
      {/*
        WEB-307 spacer: reserves layout space so the fixed banner below does
        not overlap content. `aria-hidden` keeps assistive tech focused on the
        real banner.
      */}
      <div
        data-testid="cookie-consent-spacer"
        aria-hidden="true"
        style={{ height: bannerHeight }}
        className="w-full shrink-0"
      />
      <div
        ref={bannerRef}
        role="dialog"
        aria-label="Cookie and analytics consent"
        className={cn(
          // Flush to the viewport bottom at all sizes. Safe-area inset grows
          // bottom padding (not `bottom`) so the banner edge stays aligned
          // with the viewport while content clears the home indicator.
          //
          // FRE-1287 / WEB-611 / WEB-624: sit ABOVE Locus chrome
          // (`--lx-z` → `--z-sheet`: 80 on `.lx-launcher` / panel). A lower
          // Tailwind `z-50` (50) previously let Ask LOCI intercept Accept on
          // marketing-city event detail — `z-[1300]` is intentional so the
          // legal gate stays above all Locus + app chrome (Decline/Accept
          // clickable and keyboard-reachable on every CookieConsent surface).
          'fixed bottom-0 left-0 right-0 z-[1300] flex flex-col gap-3 border-t bg-background p-4 shadow-lg',
          'supports-[padding:env(safe-area-inset-bottom)]:pb-[calc(1rem+env(safe-area-inset-bottom))]',
          'sm:flex-row sm:items-center sm:justify-between sm:gap-4',
        )}
      >
        <p className="text-sm text-muted-foreground">
          We use analytics to improve your experience and understand how you use LOCI. You can
          accept or decline.
        </p>
        <div className="flex shrink-0 gap-2">
          {/* WEB-306 / WEB-1006: the DS sm button carries the 44×44 coarse-pointer
              hit box as an invisible `::before`, so the tap target meets WCAG 2.5.5
              without growing the banner's outer `p-4` padding. */}
          <Button variant="ghost" size="sm" onClick={handleReject} aria-label="Decline analytics">
            Decline
          </Button>
          <Button variant="accent" size="sm" onClick={handleAccept} aria-label="Accept analytics">
            Accept
          </Button>
        </div>
      </div>
    </>
  );
}
